import React, { useEffect, useState } from 'react';
import FullCalendar, { EventApi } from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { DatesSetArg } from '@fullcalendar/common';
import '../../../styles/components/training-calendar.scss';
import axios from 'axios';
import { getLocaleFromHtml } from '~utils/locale';
import { getContrastBwColor } from '~utils/color';
import { changeWindowUrl, encodeQueryData, getJSONDataFromLocationSearch } from '~utils/url';
import { useViewportSize } from '../../hooks';
import Filter, { FilterCell } from '~react/components/Calendar/components/Filter';
import { BasicObject } from '~components/ObjectHelper';
import Actions, { ActionData } from '~react/components/Calendar/components/Actions';

interface CalendarProps {
    config: {
        apiUrl: string;
        filters: FilterCell[];
        actions?: ActionData[];
        weekends?: boolean;
    };
}

interface EventsDataType {
    title: string;
    start: string;
    end: string;
    linkUrl: string;
    color: string;
    classNames: string[];
}

const Calendar: React.FC<CalendarProps> = props => {
    const defaultWeekends = props.config.weekends ?? true;
    const calendarComponentRef = React.useRef<FullCalendar>(null);

    const [calendarEvents, setCalendarEvents] = useState<EventsDataType[]>([]);
    const [currentDate, setCurrentDate] = useState(new Date());
    const [filter, setFilter] = useState<BasicObject>({});
    const viewportWidth = useViewportSize();
    const [weekends, setWeekends] = useState<boolean>(defaultWeekends);

    useEffect(() => {
        const searchData: BasicObject = getJSONDataFromLocationSearch('');

        const newFilter = filter;
        props.config.filters.map(item => {
            newFilter[item.name] = item.defaultValue;
        });

        Object.keys(searchData).map(key => {
            newFilter[key] = searchData[key];
        });

        setFilter({ ...newFilter });
    }, []);

    useEffect(() => {
        if (undefined !== filter.month && '' !== filter.month) {
            setCurrentDate(new Date(Date.parse(filter.month + '-15')));
        }
        loadEvents();
        changeWindowUrl('?' + encodeQueryData(filter));
    }, [filter]);

    useEffect(() => {
        setComponentCalendarDate();
    }, [currentDate]);

    const isWeekend = (dateString: string): boolean => {
        const date = new Date(dateString);
        const dayOfWeek = date.getDay();
        return dayOfWeek === 6 || dayOfWeek === 0;
    };

    const loadEvents = (): void => {
        setCalendarEvents([]);
        setWeekends(defaultWeekends);
        axios
            .get<{ events: EventsDataType[] }>(props.config.apiUrl, {
                params: filter,
            })
            .then(response => {
                if (200 === response.status) {
                    const events = response.data.events;
                    setCalendarEvents(events);
                    setWeekends(defaultWeekends || events.some(event => isWeekend(event.start)));
                }
            })
            .catch(error => {
                console.error(error);
            });
    };

    const setComponentCalendarDate = (): void => {
        if (null === calendarComponentRef?.current) {
            return;
        }
        const calendarApi = calendarComponentRef.current.getApi();
        calendarApi.gotoDate(currentDate);
    };

    const handleDateSet = (arg: DatesSetArg): void => {
        setCurrentDate(arg.view.currentStart);
    };

    const eventRender = (info: any) => {
        const event: EventApi = info.event;
        return (
            <span
                className="event-inner"
                style={{
                    backgroundColor: event.backgroundColor,
                    color: getContrastBwColor(event.backgroundColor),
                }}
            >
                <span>{event.title}</span>
            </span>
        );
    };

    const onFilterChange = (name: string, value: string) => {
        const newFilter = filter;
        newFilter[name] = value;
        setFilter({ ...newFilter });
    };

    const weekdayFormat = undefined === viewportWidth || 768 > viewportWidth ? 'short' : 'long';

    return (
        <div className="training-calendar">
            <Filter cells={props.config.filters} onChange={onFilterChange} values={filter} />
            <FullCalendar
                plugins={[dayGridPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                    start: '',
                    center: '',
                    end: '',
                }}
                locale={getLocaleFromHtml()}
                firstDay={1}
                eventContent={eventRender}
                datesSet={handleDateSet}
                buttonText={{ today: 'dnes' }}
                events={calendarEvents}
                ref={calendarComponentRef}
                dayHeaderFormat={{
                    weekday: weekdayFormat,
                }}
                contentHeight="auto"
                fixedWeekCount={false}
                weekends={weekends}
            />
            {props.config.actions ? <Actions actions={props.config.actions} filter={filter} /> : ''}
        </div>
    );
};

export default Calendar;
export { EventsDataType };
